import type { WorkbookDashboard } from "../../../shared/account-finance/finance-workbook-contract";
import { convertWorkbookGroups } from "./finance-workbook-conversion";
import type { DisplayCurrency } from "./finance-workbook-conversion";
import { workbookCurrencies, workbookUniqueRows } from "./finance-workbook-presentation";
import { financeAvailabilityIssues, financeCashCurrencies, financeCashSources, financeOpeningBalance, financePlanLabels, financeSourceIssue, financeSourceRows,
  financeTimelineMonths, financeTimelinePeriods } from "./finance-workbook-query";
import type { FinanceCashBasis, FinanceGranularity, FinancePeriod, FinanceSourceIssue } from "./finance-workbook-query";

export { financeTimelineMonths, financeTimelinePeriods } from "./finance-workbook-query";
export type { FinanceCashBasis, FinanceGranularity, FinancePeriod } from "./finance-workbook-query";
export type FinanceMonth = FinancePeriod;
export type FinanceTimelineRow = {
  id: string; label: string; sheetId?: string;
  kind: "opening" | "incoming" | "outgoing" | "net" | "closing" | "total" | "reference";
  included?: boolean; overdue?: bigint | null; undated?: bigint | null;
  values: (bigint | null)[];
  issues?: FinanceSourceIssue[];
};
export type FinancePlanCoverage = {
  category: string; label: string; period: FinancePeriod; status: "missing" | "zero" | "planned";
  amounts: { currency: string; amount: bigint }[];
};
export type FinanceCashDay = {
  date: string; opening: bigint | null; incoming: bigint | null; outgoing: bigint | null;
  net: bigint | null; closing: bigint | null;
};
export type FinanceCashInsight = {
  currency: string; opening: bigint | null; complete: boolean; daily: FinanceCashDay[];
  next7: { from: string; to: string; incoming: bigint | null; outgoing: bigint | null; net: bigint | null; closing: bigint | null; complete: boolean };
  firstDeficit: { date: string; amount: bigint } | null;
  minimum: { date: string; amount: bigint } | null;
};

/** A historical or incoming plan never satisfies a future outgoing category. A bound plan is still a plan, not an explicit zero. */
export function financePlanCoverage(book: WorkbookDashboard, periods = financeTimelineMonths(book.date)): FinancePlanCoverage[] {
  const sheet = book.sheets.find(item => item.id === "manuel-plan");
  const rows = sheet && !financeSourceIssue(book, sheet.id) ? workbookUniqueRows(sheet).filter(row => row.direction === "outgoing") : [];
  return periods.flatMap(period => Object.entries(financePlanLabels).map(([category, label]) => {
    const selected = rows.filter(row => row.code === category && row.date && row.date >= period.from && row.date <= period.to);
    const currencies = [...new Set(selected.map(row => row.currency))];
    const amounts = currencies.map(currency => ({ currency, amount: selected.filter(row => row.currency === currency)
      .reduce((sum, row) => sum + BigInt(row.remaining), 0n) }));
    const status = !selected.length ? "missing" : selected.every(row => BigInt(row.amount ?? row.remaining) === 0n) ? "zero" : "planned";
    return { category, label, period, status, amounts };
  }));
}

/** months remains an alias of periods for existing callers; the third argument selects the actual aggregation. */
export function financeWorkbookTimeline(book: WorkbookDashboard, includePlans = false, granularity: FinanceGranularity = "month", basis: FinanceCashBasis = "recorded") {
  const periods = financeTimelinePeriods(book.date, granularity), sources = financeCashSources(book, includePlans);
  const currencies = financeCashCurrencies(book, includePlans);
  const groups = currencies.map(currency => {
    const rows: FinanceTimelineRow[] = sources.filter(source => source.kind !== "opening").map(source => {
      const sheet = book.sheets.find(item => item.id === source.sheetId);
      const records = financeSourceRows(book, source).filter(row => row.currency === currency);
      const unallocated = records.some(row => row.date == null && BigInt(row.remaining) !== 0n);
      const blockingUndated = unallocated && sheet?.kind !== "open";
      const issues: FinanceSourceIssue[] = source.issue ? [source.issue] : [];
      if (unallocated) issues.push({ sheetId: source.sheetId, kind: "undated", message: "Vadesi bilinmeyen kayıtlar bu döneme yerleştirilemedi." });
      const sumOutside = (undated: boolean) => !source.available ? null : records.filter(row => undated
        ? row.date == null : row.date != null && row.date < book.date).reduce((sum, row) => sum + BigInt(row.remaining), 0n);
      return {
        id: source.id, sheetId: source.sheetId, label: source.label, kind: source.kind, included: source.included,
        overdue: sumOutside(false), undated: sumOutside(true), issues,
        values: periods.map(period => !source.available || blockingUndated ? null : records.reduce((sum, row) => {
          if (!row.date || row.date < period.from || row.date > period.to) return sum;
          const value = BigInt(row.remaining);
          return sum + (source.kind === "outgoing" ? -value : value);
        }, 0n)),
      };
    });
    rows.sort((left, right) => Number(left.kind === "outgoing") - Number(right.kind === "outgoing"));
    const total = (index: number, kind?: "incoming" | "outgoing") => {
      const values = rows.filter(row => row.included !== false && (!kind || row.kind === kind)).map(row => row.values[index] ?? null);
      return values.some(value => value === null) ? null : values.reduce<bigint>((sum, value) => sum + (value ?? 0n), 0n);
    };
    const net = periods.map((_, index) => total(index));
    let balance = financeOpeningBalance(book, currency, basis);
    const openings: (bigint | null)[] = [], closings: (bigint | null)[] = [];
    for (const change of net) {
      openings.push(balance);
      balance = balance === null || change === null ? null : balance + change;
      closings.push(balance);
    }
    const completeRows: FinanceTimelineRow[] = [
      { id: "opening", label: "Dönem başı / devreden kayıt bakiyesi", kind: "opening", values: openings }, ...rows,
      ...(["incoming", "outgoing"] as const).map(kind => ({ id: `${kind}-total`,
        label: kind === "incoming" ? "Toplam tahsilat" : "Toplam ödeme", kind: "total" as const,
        values: periods.map((_, index) => total(index, kind)) })),
      { id: "net", label: "Dönemin net etkisi", kind: "net", values: net },
      { id: "closing", label: "Dönem sonunda kalan nakit / finansman açığı", kind: "closing", values: closings },
    ];
    return { currency, rows: completeRows };
  });
  const allCurrencies = workbookCurrencies(book.sheets.filter(sheet => sources.some(source => source.sheetId === sheet.id)));
  return { months: periods, periods, groups, inactiveCurrencies: allCurrencies.filter(currency => !currencies.includes(currency)),
    coverage: financePlanCoverage(book) };
}

/** Highlights use daily balances so an intra-month cash deficit cannot be hidden by month-end receipts. */
export function financeWorkbookInsights(book: WorkbookDashboard, currency: DisplayCurrency = "TL", includePlans = false, basis: FinanceCashBasis = "recorded") {
  const timeline = financeWorkbookTimeline(book, includePlans, "day", basis);
  const converted = convertWorkbookGroups(timeline.groups, currency, book.context, book.date);
  const coverage = timeline.coverage;
  const sourceIssues = financeCashSources(book, includePlans).filter(source => source.sheetId !== "leasing" || includePlans)
    .flatMap(source => source.issue ? [source.issue] : []);
  sourceIssues.push(...financeAvailabilityIssues(book, timeline.groups.map(group => group.currency), basis));
  for (const group of timeline.groups) for (const row of group.rows) if (row.included !== false) sourceIssues.push(...row.issues ?? []);
  const issues = [...new Map(sourceIssues.map(issue => [`${issue.sheetId}:${issue.kind}`, issue])).values()];
  const sourceComplete = issues.length === 0;
  const planComplete = coverage.every(item => item.status !== "missing");
  const firstWeek = timeline.periods.slice(0, 7), from = firstWeek[0]?.from ?? book.date, to = firstWeek.at(-1)?.to ?? book.date;
  const nextPlanComplete = coverage.filter(item => item.period.from <= to && item.period.to >= from).every(item => item.status !== "missing");
  const groups: FinanceCashInsight[] = converted.map(group => {
    const value = (id: string, index: number) => group.rows.find(row => row.id === id)?.values[index] ?? null;
    const daily = timeline.periods.map((period, index) => ({ date: period.from, opening: value("opening", index),
      incoming: value("incoming-total", index), outgoing: value("outgoing-total", index) === null ? null : -(value("outgoing-total", index) ?? 0n),
      net: value("net", index), closing: value("closing", index) }));
    const complete = sourceComplete && planComplete && daily.every(day => day.closing !== null);
    const nextComplete = sourceComplete && nextPlanComplete && daily.slice(0, 7).every(day => day.closing !== null);
    const sum = (field: "incoming" | "outgoing" | "net") => nextComplete
      ? daily.slice(0, 7).reduce((total, day) => total + (day[field] ?? 0n), 0n) : null;
    const points = daily.flatMap(day => day.closing === null ? [] : [{ date: day.date, amount: day.closing }]);
    // An already-negative opening is the first financing need, even if receipts cover it later that day.
    const opening = daily[0]?.opening ?? null;
    if (opening !== null) points.unshift({ date: book.date, amount: opening });
    const firstDeficit = complete ? points.find(point => point.amount < 0n) ?? null : null;
    const minimum = complete ? points.reduce<{ date: string; amount: bigint } | null>((min, point) => !min || point.amount < min.amount ? point : min, null) : null;
    return { currency: group.currency, opening, complete, daily,
      next7: { from, to, incoming: sum("incoming"), outgoing: sum("outgoing"), net: sum("net"),
        closing: nextComplete ? daily[6]?.closing ?? null : null, complete: nextComplete }, firstDeficit, minimum };
  });
  return { groups, complete: groups.every(group => group.complete), issues, coverage };
}
